import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type SettingsContextValueProps = {};

export interface UserSettings {
  annualExerciseCount?: number,
  annualProblemsList?: string[],
  bg?: string,
  difficulty?: number,
  id?: string,
  level?: string,
  selectedLevel?: string
}

export type SettingsContextProps = SettingsContextValueProps & {
  profileData: any;
  setProfileData: (value: any) => void;
  settings: UserSettings;
  onSetSettings: (settings: any) => void;
  avatar: string | undefined;
  setAvatar: (avatar: string) => void;
  skin: any;
  setSkin: (skin: any) => void;
  onUpdateSettings: (field: string, value: string | boolean) => void;
  settingModalOpen: boolean;
  setSettingModalOpen: (open: boolean) => void;
  profileOpen: boolean;
  setProfileOpen: (open: boolean) => void;
  defaultProfileTab: number;
  setDefaultProfileTab: (tabIndex: number) => void;
  settingsTabIndex: number;
  setSettingsTabIndex: (tabIndex: number) => void;
};

const initialState: SettingsContextProps = {
  profileData: {},
  setProfileData: () => {},
  settings: {},
  onSetSettings: () => {},
  avatar: undefined,
  setAvatar: () => {},
  skin: null,
  setSkin: () => {},
  onUpdateSettings: () => {},
  settingModalOpen: false,
  setSettingModalOpen: () => {},
  profileOpen: false,
  setProfileOpen: () => {},
  defaultProfileTab: 0,
  setDefaultProfileTab: () => {},
  settingsTabIndex: 0,
  setSettingsTabIndex: () => {},
};

export const SettingsContext = createContext(initialState);

type SettingsProviderProps = {
  children: ReactNode;
};

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [profileData, setProfileData] = useState<any>();
  const [settings, setSettings] = useState({});
  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [defaultProfileTab, setDefaultProfileTab] = useState(0);
  const [settingsTabIndex, setSettingsTabIndex] = useState(0);
  const [skin, setUserSkin] = useState();
  const [avatar, setAvatar] = useState<string>();

  const onSetSettings = useCallback((settings) => {setSettings(settings)}, [setSettings, settings]);
  const setSkin = useCallback((skin: any) => {setUserSkin(skin)}, [setUserSkin]);

  const onUpdateSettings = useCallback(
    (field: string, value: string | boolean) => {
      setSettings((currentSettings) => ({
        ...currentSettings,
        [field]: value,
      }));
    },
    [setSettings, settings]
  );

  const contextValue = useMemo(
    () => ({
      profileData,
      setProfileData,
      settings,
      onSetSettings,
      avatar,
      setAvatar,
      skin,
      setSkin,
      onUpdateSettings,
      settingModalOpen,
      setSettingModalOpen,
      profileOpen,
      setProfileOpen,
      defaultProfileTab,
      setDefaultProfileTab,
      settingsTabIndex,
      setSettingsTabIndex,
    }),
    [
      profileData,
      setProfileData,
      onSetSettings,
      skin,
      setSkin,
      onUpdateSettings,
      settings,
      settingModalOpen,
      profileOpen,
      setProfileOpen,
      defaultProfileTab,
      setDefaultProfileTab,
      settingsTabIndex,
      setSettingsTabIndex,
    ]
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettingsContext = () => useContext(SettingsContext);
